import React from 'react';
import { MapNode } from './useMapLayout';

interface Props {
  node: MapNode;
  transform: { x: number; y: number; scale: number };
  canvasWidth: number;
  canvasHeight: number;
  darkMode: boolean;
}

const ControllerPromptOverlay: React.FC<Props> = ({
  node, transform, canvasWidth, canvasHeight, darkMode,
}) => {
  const toScreen = (wx: number, wy: number) => ({
    sx: wx * transform.scale + transform.x,
    sy: wy * transform.scale + transform.y,
  });

  const { sx: cx, sy: cy } = toScreen(node.x, node.y);
  const screenR = node.radius * transform.scale;

  if (cx + screenR < 0 || cx - screenR > canvasWidth ||
      cy + screenR < 0 || cy - screenR > canvasHeight) return null;

  const prompt: string = node.data || '';
  const tokenEstimate = Math.round(prompt.length / 4);
  const preview = prompt.length > 200 ? prompt.slice(0, 200) + '…' : prompt;
  const cardW = Math.max(160, Math.min(260, screenR * 2.0));

  return (
    <div
      style={{
        position: 'absolute',
        left: cx - cardW / 2,
        top: cy + screenR + 6,
        width: cardW,
        pointerEvents: 'none',
        zIndex: 30,
      }}
    >
      <div className={`rounded-xl border shadow-xl overflow-hidden ${
        darkMode ? 'bg-gray-900/95 border-emerald-800' : 'bg-white border-emerald-300'
      }`}>
        {/* Header */}
        <div className={`px-3 py-1.5 flex items-center gap-2 border-b ${
          darkMode ? 'bg-emerald-900/40 border-emerald-800' : 'bg-emerald-50 border-emerald-200'
        }`}>
          <span style={{ fontSize: 10 }}>⚙</span>
          <span className={`font-bold uppercase tracking-wide ${
            darkMode ? 'text-emerald-300' : 'text-emerald-700'
          }`} style={{ fontSize: 9 }}>
            Controller Prompt
          </span>
          <span className={`ml-auto tabular-nums ${
            darkMode ? 'text-gray-500' : 'text-gray-400'
          }`} style={{ fontSize: 8 }}>
            ~{tokenEstimate.toLocaleString()} tok
          </span>
        </div>

        {/* Preview */}
        <div className={`px-3 py-2 font-mono leading-relaxed ${
          darkMode ? 'text-gray-400' : 'text-gray-600'
        }`} style={{ fontSize: 8, whiteSpace: 'pre-wrap', maxHeight: 90, overflow: 'hidden' }}>
          {preview || <span className="italic opacity-50">Empty — generate controls first</span>}
        </div>
      </div>
    </div>
  );
};

export default ControllerPromptOverlay;
